/**
 * Workflow Template Validation State Types
 * 
 * Centralized state management for workflow template validation.
 * This module defines the types used by the ValidationStateManager to track
 * validity, dirty state, and mid-edit state across all tabs.
 * 
 * @see docs/workflow-template-validation-plan.md for full requirements
 */

/**
 * Validation state for a single tab
 */
export interface TabValidationState {
    /** Whether all required fields are filled and valid */
    valid: boolean;
    
    /** Whether the tab has unsaved changes (compared to original data) */
    dirty: boolean;
    
    /** 
     * Whether the tab has an active edit in progress
     * Examples: variable row being edited, notification row being added, step wizard open
     * When true, the template cannot be saved.
     */
    midEdit: boolean;
}

/**
 * Tab names used throughout the validation system
 */
export type TabName = 'settings' | 'workflowInfo' | 'notification' | 'steps' | 'scheduler';

/**
 * Transfer type affects scheduler requirement
 */
export type TransferType = 'Push' | 'Pull' | '';

/**
 * Mode the template is being accessed in
 */
export type TemplateMode = 'ADD' | 'EDIT' | 'VIEW';

/**
 * Variable usage tracking - maps variable name to locations where it's used
 */
export interface VariableUsage {
    /** Variable name */
    variableName: string;
    
    /** Locations where this variable is used (e.g., "workflowInfo.workflowName", "notification.row1.email") */
    locations: string[];
}

/**
 * Represents a variable reference found in a template-string-input field
 */
export interface VariableReference {
    /** The variable name as defined in Settings (no `{` `}` delimiters) */
    name: string;
    
    /** The tab where this variable is used */
    tab: TabName;
    
    /** The field path where this variable is used */
    fieldPath: string;
    
    /** Whether this variable exists in the Settings tab */
    isDefined: boolean;
}

/**
 * Event detail for validation-change events from tabs
 */
export interface ValidationChangeEventDetail {
    /** Which tab is reporting */
    tabName: TabName;
    
    /** Whether the tab is valid */
    valid: boolean;
    
    /** Whether the tab has unsaved changes */
    dirty?: boolean;
    
    /** Whether the tab has a mid-edit state (row/wizard being edited) */
    midEdit?: boolean;
    
    /** Transfer type (only from workflowInfo tab) */
    transferType?: TransferType;
    
    /** Current variables list (only from settings tab) */
    variables?: Array<{ name: string }>;
}

/**
 * Complete validation state for the entire workflow template
 */
export interface WorkflowTemplateValidationState {
    /** Current mode */
    mode: TemplateMode;
    
    /** Per-tab validation state */
    tabs: {
        settings: TabValidationState;
        workflowInfo: TabValidationState;
        notification: TabValidationState;
        steps: TabValidationState;
        scheduler: TabValidationState;
    };
    
    /** Current transfer type (affects scheduler requirement) */
    transferType: TransferType;
    
    /** List of defined variable names (from settings tab) */
    definedVariables: string[];
    
    /** Tracking of where each variable is used */
    variableUsage: Map<string, string[]>;
    
    /** Whether any tab has undefined variable references */
    hasUndefinedVariables: boolean;
    
    // ===== Computed Properties =====
    
    /** True if all required tabs are valid and no mid-edit states */
    readonly allValid: boolean;
    
    /** True if any tab has unsaved changes */
    readonly anyDirty: boolean;
    
    /** True if any tab has a mid-edit state */
    readonly anyMidEdit: boolean;
    
    /** True if the Save button should be enabled */
    readonly canSave: boolean;
    
    /** True if there are unsaved changes (for navigation warning) */
    readonly hasUnsavedChanges: boolean;
}

/**
 * Default state for a single tab
 */
export const DEFAULT_TAB_STATE: TabValidationState = {
    valid: false,
    dirty: false,
    midEdit: false,
};

/**
 * Default state for optional tabs (valid by default since they're optional)
 */
export const DEFAULT_OPTIONAL_TAB_STATE: TabValidationState = {
    valid: true,
    dirty: false,
    midEdit: false,
};

/**
 * Initial validation state for a new template (ADD mode)
 */
export function createInitialValidationState(mode: TemplateMode = 'ADD'): WorkflowTemplateValidationState {
    const isViewMode = mode === 'VIEW';
    
    return {
        mode,
        tabs: {
            settings: { ...DEFAULT_TAB_STATE, valid: isViewMode },
            workflowInfo: { ...DEFAULT_TAB_STATE, valid: isViewMode },
            notification: { ...DEFAULT_OPTIONAL_TAB_STATE },
            steps: { ...DEFAULT_OPTIONAL_TAB_STATE },
            scheduler: { ...DEFAULT_OPTIONAL_TAB_STATE },
        },
        transferType: '',
        definedVariables: [],
        variableUsage: new Map(),
        hasUndefinedVariables: false,
        
        // Computed properties - these are calculated by the state manager
        get allValid() {
            return calculateAllValid(this);
        },
        get anyDirty() {
            return calculateAnyDirty(this);
        },
        get anyMidEdit() {
            return calculateAnyMidEdit(this);
        },
        get canSave() {
            return calculateCanSave(this);
        },
        get hasUnsavedChanges() {
            return this.anyDirty || this.anyMidEdit;
        },
    };
}

/**
 * Calculate if all tabs are valid
 */
function calculateAllValid(state: WorkflowTemplateValidationState): boolean {
    const { tabs, transferType, hasUndefinedVariables } = state;
    
    // Check all required tabs
    const settingsValid = tabs.settings.valid && !tabs.settings.midEdit;
    const workflowInfoValid = tabs.workflowInfo.valid && !tabs.workflowInfo.midEdit;
    const notificationValid = tabs.notification.valid && !tabs.notification.midEdit;
    const stepsValid = tabs.steps.valid && !tabs.steps.midEdit;
    
    // Scheduler is only required for Pull type
    const schedulerRequired = transferType === 'Pull';
    const schedulerValid = schedulerRequired 
        ? (tabs.scheduler.valid && !tabs.scheduler.midEdit)
        : true;
    
    return settingsValid && 
           workflowInfoValid && 
           notificationValid && 
           stepsValid && 
           schedulerValid &&
           !hasUndefinedVariables;
}

/**
 * Calculate if any tab has unsaved changes
 */
function calculateAnyDirty(state: WorkflowTemplateValidationState): boolean {
    const { tabs } = state;
    return tabs.settings.dirty ||
           tabs.workflowInfo.dirty ||
           tabs.notification.dirty ||
           tabs.steps.dirty ||
           tabs.scheduler.dirty;
}

/**
 * Calculate if any tab has a mid-edit state
 */
function calculateAnyMidEdit(state: WorkflowTemplateValidationState): boolean {
    const { tabs } = state;
    return tabs.settings.midEdit ||
           tabs.workflowInfo.midEdit ||
           tabs.notification.midEdit ||
           tabs.steps.midEdit ||
           tabs.scheduler.midEdit;
}

/**
 * Calculate if the Save button should be enabled
 */
function calculateCanSave(state: WorkflowTemplateValidationState): boolean {
    // VIEW mode: can never save
    if (state.mode === 'VIEW') {
        return false;
    }
    
    // ADD mode: can save when all valid (dirty is implied since user entered data)
    // EDIT mode: can save when all valid AND has changes
    const isValid = calculateAllValid(state);
    const hasChanges = state.mode === 'ADD' || calculateAnyDirty(state);
    
    return isValid && hasChanges && !calculateAnyMidEdit(state);
}
